// The broker trading views (SM-823): the Users → Details tabs (SM-828) and
// the sidebar Trading pages (SM-829). What the tabs are, which positions
// count as active, how often the live views poll, and how a row's side and
// price are read. Presentation only — never arithmetic on a figure.
//
// Relative imports only: node --test runs this file directly.
import { formatPrice } from "./money.ts";
import type { AccountScope } from "./types.ts";

/**
 * The Users → Details tabs, in strip order (?tab=). The last two are
 * SM-822's request logs — where each login and order came FROM — which are
 * not the trading views: "order-log" (IP and device per order) and
 * "history" (each finished order's trading state) are different data.
 */
export const USER_TABS = ["orders", "positions", "history", "trades", "login-log", "order-log"] as const;
export type UserTab = (typeof USER_TABS)[number];
export const DEFAULT_USER_TAB: UserTab = "orders";

export function isUserTab(v: unknown): v is UserTab {
  return typeof v === "string" && (USER_TABS as readonly string[]).includes(v);
}

// --- the Scope switch (Users and the four Trading pages, SM-862) -------------

/**
 * Every Scope switch opens on the whole subtree, as Settlement does: a
 * root that services no users itself would otherwise open Users and every
 * Trading page empty.
 */
export const DEFAULT_ACCOUNT_SCOPE: AccountScope = "subtree";

/** ?scope= as the URL keeps it: only the non-default "direct" is written. */
export type ScopeSearch = "direct";

/** ?scope= → its URL value; "subtree" (an old link) reads the same as no param. */
export function parseScopeSearch(v: unknown): ScopeSearch | undefined {
  return v === "direct" ? "direct" : undefined;
}

/** The scope a page reads for its ?scope=. */
export function scopeFromSearch(v: ScopeSearch | undefined): AccountScope {
  return v ?? DEFAULT_ACCOUNT_SCOPE;
}

/** The ?scope= a choice writes: the default leaves the URL. */
export function scopeToSearch(scope: AccountScope): ScopeSearch | undefined {
  return scope === DEFAULT_ACCOUNT_SCOPE ? undefined : "direct";
}

/** The four trading views a Trading page links back to on Users → Details. */
export type TradingTab = "orders" | "positions" | "history" | "trades";

/**
 * "Active positions": OPEN and REDEEMABLE, sent as ONE comma list
 * (status=OPEN,REDEEMABLE; settlement-service SM-826 parses it, broker-service
 * SM-827 passes it on).
 */
export const ACTIVE_POSITION_STATUSES = "OPEN,REDEEMABLE";

/** Every position status settlement-service reports, for a single-status filter. */
export const POSITION_STATUSES = ["OPEN", "CLOSED", "REDEEMABLE", "REDEEMED", "LOST", "REFUNDED", "RESOLVED"] as const;

/**
 * The Active positions page's ?status=: absent = active (the default),
 * "ALL" = every status, else one status. "ALL" is a URL value only, so the
 * choice survives a reload; it is never sent.
 */
export const POSITION_STATUS_ALL = "ALL";

export function parsePositionStatus(v: unknown): string | undefined {
  if (v === POSITION_STATUS_ALL) return POSITION_STATUS_ALL;
  return typeof v === "string" && (POSITION_STATUSES as readonly string[]).includes(v) ? v : undefined;
}

/** The status a positions read sends for the page's ?status= (undefined = none: every status). */
export function positionStatusParam(urlStatus: string | undefined): string | undefined {
  if (urlStatus === undefined) return ACTIVE_POSITION_STATUSES;
  if (urlStatus === POSITION_STATUS_ALL) return undefined;
  return urlStatus;
}

/** The terminal statuses Order history filters on, one at a time (wire values: CANCELED has one L). */
export const ORDER_HISTORY_STATUSES = ["FILLED", "CANCELED", "REJECTED", "EXPIRED", "FAILED"] as const;

export function parseOrderHistoryStatus(v: unknown): string | undefined {
  return typeof v === "string" && (ORDER_HISTORY_STATUSES as readonly string[]).includes(v) ? v : undefined;
}

/** Active orders and active positions refetch this often; history and trades never poll. */
export const ACTIVE_POLL_MS = 30_000;

/**
 * Side and price as the user placed them: the intent pair when the row
 * carries both, otherwise the book pair — never one of each, so a row reads
 * either fully as placed ("Buy NO @ 75¢") or fully as matched (the book's
 * "Sell @ 25¢"). The side is upper-cased for its label.
 */
export function asPlaced(row: { side: string; price: string; intent_side?: string; intent_price?: string }): {
  side: string;
  price: string;
} {
  if (row.intent_side && row.intent_price) return { side: row.intent_side.toUpperCase(), price: row.intent_price };
  return { side: (row.side ?? "").toUpperCase(), price: row.price };
}

/**
 * An order or fill price (4dp price minor, 1.00 = "10000") on the 0–1 scale
 * at the fewest decimals that show it exactly, 2 to 4 — the price is its own
 * tick for formatPrice — so a whole-cent price reads "0.75" and a sub-cent
 * tick keeps its digits ("0.755") instead of rounding. No "$" (SM-985).
 */
export function priceText(minor: string | undefined | null, locale?: string): string {
  return formatPrice(minor, minor ?? undefined, locale);
}

/** An opaque id cut to its head and tail ("bac_01JA…0PQA"); short ids stay whole. */
export function shortId(id: string): string {
  return id.length <= 14 ? id : `${id.slice(0, 8)}…${id.slice(-4)}`;
}

// --- the history window (Order history, Trade history) -------------------------
//
// [from, to) in unix ms, in whole days of the READER's calendar — the times
// in the tables are local too. Node-wide reads refuse a window over 92 days
// and default to 30 when the client sends none; the pages always send both
// ends, so the window on screen is the one that was read.

export const HISTORY_DEFAULT_DAYS = 30;
export const HISTORY_MAX_DAYS = 92;
/** The period presets; 92 itself is reachable through a custom range. */
export const HISTORY_PRESET_DAYS = [7, 30, 90] as const;
export const DAY_MS = 86_400_000;

export interface HistoryWindow {
  from: number;
  to: number;
}

/** Local midnight `days` days after the day `ms` falls in (negative = before). */
export function localDayStart(ms: number, days = 0): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days).getTime();
}

/** The last `days` whole local days, today included: [midnight days − 1 ago, tomorrow's midnight). */
export function lastDaysWindow(days: number, now: number): HistoryWindow {
  return { from: localDayStart(now, 1 - days), to: localDayStart(now, 1) };
}

/** Whether a window can be read: it ends after it starts and spans at most 92 days. */
export function windowFits(w: HistoryWindow): boolean {
  return w.to > w.from && w.to - w.from <= HISTORY_MAX_DAYS * DAY_MS;
}

/** The window a page reads: the URL's when both ends are there and it fits, else the last 30 days. */
export function resolveWindow(from: number | undefined, to: number | undefined, now: number): HistoryWindow {
  if (from !== undefined && to !== undefined && windowFits({ from, to })) return { from, to };
  return lastDaysWindow(HISTORY_DEFAULT_DAYS, now);
}

/** "YYYY-MM-DD" (a date input's value) → that local day's midnight, or null. */
export function localDateInputToMs(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // reject a rolled-over date such as 2026-02-31
  if (d.getFullYear() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3])) return null;
  return d.getTime();
}

/** unix ms → "YYYY-MM-DD" of its local day. */
export function msToLocalDateInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * A typed range of whole local days, the end day included:
 * ("2026-09-01", "2026-09-30") → [Sep 1 00:00, Oct 1 00:00), or why not.
 */
export function dayRangeWindow(
  fromDay: string,
  toDay: string,
): { ok: true; window: HistoryWindow } | { ok: false; error: "bad" | "tooLong" } {
  const from = localDateInputToMs(fromDay);
  const toStart = localDateInputToMs(toDay);
  if (from === null || toStart === null) return { ok: false, error: "bad" };
  const window = { from, to: localDayStart(toStart, 1) };
  if (window.to <= window.from) return { ok: false, error: "bad" };
  if (!windowFits(window)) return { ok: false, error: "tooLong" };
  return { ok: true, window };
}

/** The preset a window is, when it is one (for highlighting); undefined for a custom range. */
export function presetOf(w: HistoryWindow, now: number): (typeof HISTORY_PRESET_DAYS)[number] | undefined {
  return HISTORY_PRESET_DAYS.find((days) => {
    const p = lastDaysWindow(days, now);
    return p.from === w.from && p.to === w.to;
  });
}
